/**
 * Admin User Editor data access (AR-327).
 *
 * `GET /api/admin/users/:id/record` returns one member assembled from every
 * table that already stores part of them. Sections with no schema home yet
 * (sales figures, onboarding documents, admin notes, plan-change history) are
 * absent from the payload on purpose — the editor renders those as
 * pending-integration states instead of showing invented values.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';

export interface AdminRecordAddress {
  addressId: number;
  addressType: 'billing' | 'mailing';
  street: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

export interface AdminRecordExternalId {
  linkId: number;
  systemName: string;
  externalId: string;
  status: string;
  connectedAt: string | null;
}

export interface AdminRecordTramsAccount {
  id: number;
  profileId: number;
  corporateId: number | null;
  agentName: string;
  interfaceId: string;
  isDefault: boolean;
}

export interface AdminRecordTeamMember {
  id: number;
  displayName: string;
  email: string;
  roleSlug: string | null;
  isActive: boolean;
  avatarUrl: string | null;
}

export interface AdminRecordSales {
  lastYearCommission: number;
  ytdCommission: number;
  lastYearSales: number;
  ytdSales: number;
}

/**
 * AR-382 · the migrated-model block. Present only when the `proMigration`
 * flag is on for the requesting admin; its presence is what binds the editor
 * to the migrated columns and enables Save.
 */
export interface AdminRecordMigrated {
  user: {
    uuid: string | null;
    accountStatus: string | null;
    lastVerifiedAt: string | null;
    isTestAccount: boolean;
    fullLegalName: string | null;
    referredBy: string | null;
  };
  profile: {
    nickname: string | null;
    secondaryPhone: string | null;
    employmentType: string | null;
    tshirtSize: string | null;
    companyLegalName: string | null;
    companyBrandName: string | null;
    companyEin: string | null;
    slug: string | null;
    showcasePublishState: string | null;
  };
  contactType: string | null;
  trio: { isEnabled: boolean; eprId: string | null } | null;
  websites: { provider: string; publicUrl: string | null; publishState: string | null }[];
  sales: {
    individual: AdminRecordSales;
    agency: AdminRecordSales | null;
    asOf: string;
    basis: string;
  };
  /** Draft keys the server saves. Every other field renders read-only. */
  writable: string[];
  unbound: { section: string; field: string; note: string }[];
}

export interface AdminUserRecord {
  user: {
    id: number;
    email: string;
    displayName: string;
    avatarUrl: string | null;
    role: string;
    roleSlug: string | null;
    isActive: boolean;
    subscriptionTier: string;
    fullLegalName: string | null;
    phone: string | null;
    referredBy: string | null;
    agencyId: string | null;
    teamId: number | null;
    parentUserId: number | null;
    worldviaUserId: string | null;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    gdsInterest: boolean;
    onboardingStatus: string | null;
    createdAt: string;
    updatedAt: string;
  };
  profile: {
    firstName: string | null;
    lastName: string | null;
    displayName: string | null;
    salutation: string | null;
    bio: string | null;
    gemStatus: string | null;
    memberSince: string | null;
    businessName: string | null;
    timezone: string | null;
    phone: string | null;
    website: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    instagramHandle: string | null;
    facebookPage: string | null;
    twitterHandle: string | null;
    linkedinProfile: string | null;
    worldviaLastSyncedAt: string | null;
    [key: string]: unknown;
  } | null;
  addresses: {
    billing: AdminRecordAddress | null;
    mailing: AdminRecordAddress | null;
  };
  externalIds: AdminRecordExternalId[];
  tramsAccounts: AdminRecordTramsAccount[];
  team: {
    id: number;
    name: string;
    brandName: string | null;
    agencyLegalEntityName: string | null;
    memberCount: number;
    agencyOwnerUserId: number | null;
  } | null;
  owner: { id: number; displayName: string; email: string } | null;
  members: AdminRecordTeamMember[];
  subscription: {
    subscriptionId: number;
    status: string;
    planId: number;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    couponCode: string | null;
    createdAt: string;
  } | null;
  plan: { planId: number; name: string; monthlyPrice: string | null } | null;
  availablePlans: { planId: number; name: string; monthlyPrice: string | null }[];
  onboarding: {
    status: string | null;
    routeName: string | null;
    isAssociateRoute: boolean | null;
    pathwayName: string | null;
  } | null;
  migrated?: AdminRecordMigrated;
}

export const adminUserRecordKey = (userId: number) => ['admin', 'user-record', userId];

/**
 * The narrowest shape the editor can render without throwing.
 *
 * `apiRequest` resolves to `{}` when the body is not JSON, and `typeof null`
 * and `typeof []` are both `'object'` — so a bare `typeof` check lets `null`,
 * arrays, and `{}` through. `useRecordDraft` reads `user.displayName.trim()`
 * before the page can check `isError`, so anything short of the three fields
 * it dereferences has to fail the query instead.
 */
export const isAdminUserRecord = (data: unknown): data is AdminUserRecord => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  const user = (data as { user?: unknown }).user;
  if (!user || typeof user !== 'object' || Array.isArray(user)) return false;
  const { id, email, displayName } = user as Record<string, unknown>;
  return (
    typeof id === 'number' && typeof email === 'string' && typeof displayName === 'string'
  );
};

export function useAdminUserRecord(userId: number | null) {
  return useQuery<AdminUserRecord>({
    queryKey: adminUserRecordKey(userId ?? 0),
    queryFn: async () => {
      const data = await apiRequest<AdminUserRecord>(
        'GET',
        `/api/admin/users/${userId}/record`,
      );
      if (!isAdminUserRecord(data)) {
        throw new Error('Malformed member record response');
      }
      return data;
    },
    enabled: typeof userId === 'number' && Number.isFinite(userId),
  });
}

/** Value shown where the record has nothing stored for a field. */
export const EM_DASH = '—';

export const displayValue = (value: unknown): string => {
  if (value === null || value === undefined) return EM_DASH;
  const text = String(value).trim();
  return text.length > 0 ? text : EM_DASH;
};

/** One external_system_ids row by system, e.g. `hubspot_contact`. */
export const externalIdFor = (
  record: AdminUserRecord | undefined,
  systemName: string,
): string | null =>
  record?.externalIds.find((link) => link.systemName === systemName)?.externalId ?? null;

/** The member's default TRAMS account, or their first one. */
export const primaryTramsAccount = (
  record: AdminUserRecord | undefined,
): AdminRecordTramsAccount | null =>
  record?.tramsAccounts.find((account) => account.isDefault) ??
  record?.tramsAccounts[0] ??
  null;

/** AR-382 · saves only the changed draft keys; refetches the record after. */
export function useSaveAdminUserRecord(userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fields: Record<string, string | boolean | null>) =>
      apiRequest<{ saved: number; changed: string[] }>(
        'PATCH',
        `/api/admin/users/${userId}/record`,
        { fields },
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: adminUserRecordKey(userId) }),
  });
}
